import React, { useMemo, useState } from "react";
import { ChevronDown, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { Label } from "@/components/common";
import { ShowMore } from "@/components/ShowMore";
import { Input } from "@/components/ui/input";

const PARTS = [["secu", "Sécu"], ["mutuelle", "Mutuelle"], ["alma", "Alma"]];
const ST = {
  regle: ["Réglé", "bg-emerald-50 text-emerald-700"],
  partiel: ["Partiel", "bg-amber-50 text-amber-700"],
  attente: ["Non réglé", "bg-rose-50 text-rose-700"],
};
const FILTERS = [["all", "Tous"], ["attente", "Non réglés"], ["partiel", "Partiels"], ["regle", "Réglés"]];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const monthLabel = (m) => `${MONTHS[+m.slice(5, 7) - 1]} ${m.slice(0, 4)}`;
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const statusOf = (parts) => {
  const ok = parts.filter((p) => p.received).length;
  return ok === parts.length ? "regle" : ok ? "partiel" : "attente";
};

function buildDays(daily, txs) {
  const byLine = {};
  txs.forEach((t) => { if (t.daily_line_id) byLine[t.daily_line_id] = t; });
  return daily.map((d) => {
    const pats = {};
    PARTS.forEach(([k]) => (d[`${k}_lines`] || []).forEach((ln) => {
      const key = String(ln.id).replace(/-[sma]$/, "") || ln.patient;
      const t = byLine[ln.id];
      (pats[key] = pats[key] || { name: ln.patient, parts: [] }).parts.push({ k, amount: ln.amount, received: t ? t.status === "completed" : false, tx: t });
    }));
    const patients = Object.values(pats).map((p) => ({ ...p, status: statusOf(p.parts) }));
    return { d, patients, status: patients.length ? statusOf(patients.flatMap((p) => p.parts)) : null };
  }).filter((x) => x.patients.length);
}

const Badge = ({ s, testid }) => <span data-testid={testid} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${ST[s][1]}`}>{ST[s][0]}</span>;

function PatientLine({ p, id }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-2" data-testid={`recap-patient-${id}`}>
      <p className="min-w-0 flex-1 truncate text-sm font-semibold">{p.name}</p>
      {p.parts.map((x) => (
        <span key={x.k} className={`rounded-lg px-2 py-0.5 text-xs ${x.received ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
          {PARTS.find((q) => q[0] === x.k)[1]} {fmtEUR2(x.amount)} · {x.received ? "reçu" : "en attente"}
        </span>
      ))}
      <Badge s={p.status} testid={`recap-patient-status-${id}`} />
    </div>
  );
}

function DayCard({ x, pname, sname, filter, q }) {
  const [open, setOpen] = useState(false);
  const { d } = x;
  const shown = x.patients.filter((p) => (filter === "all" || p.status === filter) && (!q || norm(p.name).includes(norm(q))));
  const count = (s) => x.patients.filter((p) => p.status === s).length;
  return (
    <div className="py-2" data-testid={`recap-day-${d.id}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`recap-day-toggle-${d.id}`} className="flex w-full flex-wrap items-center gap-2 text-left">
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        <p className="text-sm font-semibold">{fmtDate(d.date)}</p>
        <p className="min-w-0 flex-1 truncate text-xs text-gray-500">{pname(d.practitioner_id)}{sname(d.site_id) ? ` · ${sname(d.site_id)}` : ""}</p>
        <span className="text-xs text-gray-500">{count("regle")} réglé · {count("partiel")} partiel · {count("attente")} non réglé</span>
        <Badge s={x.status} testid={`recap-day-status-${d.id}`} />
      </button>
      {open && <div className="ml-6 mt-1 divide-y divide-gray-50">{shown.map((p, i) => <PatientLine key={i} p={p} id={`${d.id}-${i}`} />)}</div>}
    </div>
  );
}

function Pick({ testid, value, onChange, all, items }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-full rounded-full sm:w-44"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const matchName = (p, q) => !q || norm(p.name).includes(norm(q));

async function exportPdf(shown, filter, q, ctx) {
  const days = shown.map((x) => ({
    date: x.d.date, practitioner: ctx.pname(x.d.practitioner_id), site: ctx.sname(x.d.site_id) || "", status: x.status,
    patients: x.patients.filter((p) => (filter === "all" || p.status === filter) && matchName(p, q))
      .map((p) => ({ name: p.name, status: p.status, parts: p.parts.map((y) => ({ label: PARTS.find((z) => z[0] === y.k)[1], amount: y.amount, received: y.received })) })),
  })).filter((d) => d.patients.length);
  try {
    const r = await api.post("/encaissements/recap-pdf", { filters: ctx.label, days }, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `recap_reglements.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error("Impossible de générer le PDF"); }
}

export function RecapReglements({ daily, txs, pname, sname, prats = [], sites = [] }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("all");
  const [prat, setPrat] = useState("all");
  const [site, setSite] = useState("all");
  const [busy, setBusy] = useState(false);
  const days = useMemo(() => buildDays(daily, txs), [daily, txs]);
  const months = Array.from(new Set(days.map((x) => x.d.date.slice(0, 7)))).sort().reverse();
  const scoped = days.filter((x) => (month === "all" || x.d.date.slice(0, 7) === month) && (prat === "all" || x.d.practitioner_id === prat) && (site === "all" || x.d.site_id === site));
  const all = scoped.flatMap((x) => x.patients);
  const tot = (s) => all.filter((p) => p.status === s).length;
  const qq = q.trim();
  const shown = scoped.filter((x) => x.patients.some((p) => (filter === "all" || p.status === filter) && matchName(p, qq)));
  const label = [month === "all" ? "Toutes périodes" : monthLabel(month), prat === "all" ? "Tous les praticiens" : pname(prat), site === "all" ? "Tous les sites" : sname(site),
    filter === "all" ? null : FILTERS.find((f) => f[0] === filter)[1], qq ? `Recherche « ${qq} »` : null].filter(Boolean).join(" · ");
  const onPdf = async () => { setBusy(true); await exportPdf(shown, filter, qq, { pname, sname, label }); setBusy(false); };
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Label>Récapitulatif des règlements Sécu / mutuelles</Label>
        <Button data-testid="recap-pdf-btn" size="sm" variant="outline" disabled={busy || !shown.length} onClick={onPdf} className="rounded-full"><FileDown className="mr-1.5 h-4 w-4" />{busy ? "Génération…" : "PDF récap"}</Button>
      </div>
      <div className="mb-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
        <Pick testid="recap-month" value={month} onChange={setMonth} all="Tous les mois" items={months.map((m) => [m, monthLabel(m)])} />
        <Pick testid="recap-practitioner" value={prat} onChange={setPrat} all="Tous les praticiens" items={prats.map((p) => [p.id, p.name])} />
        <Pick testid="recap-site" value={site} onChange={setSite} all="Tous les sites" items={sites.map((s) => [s.id, s.name])} />
        <Input data-testid="recap-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un patient…" className="h-9 w-full rounded-full sm:w-56" />
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`recap-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === k ? "bg-[#065F46] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            {l} ({k === "all" ? all.length : tot(k)})
          </button>
        ))}
      </div>
      {shown.length === 0 ? <p className="py-2 text-sm text-gray-400" data-testid="recap-empty">Aucun soin ne correspond.</p>
        : <ShowMore items={shown} testid="recap-days" initial={qq ? 50 : 6} render={(x) => <DayCard key={x.d.id} x={x} pname={pname} sname={sname} filter={filter} q={qq} />} />}
    </div>
  );
}
